// ----------------------------------------------------------------------
// <copyright file="Izenda.QuerySourceTypeList.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * QuerySourceTypeList component
 */

import React, { Component } from 'react';

import classnames from 'classnames';
import CommonMessages from 'share/messages/Izenda.CommonMessages';
import constants from 'logic/data/Izenda.DataModelConstants';
import { TableDefinition, DETECTED_CHANGE_LEVEL } from 'logic/data/Izenda.DataModelConstants';
import QuerySourceTypeConfig from 'logic/data/Izenda.QuerySourceTypeConfig';
import { Table, Column } from 'share/components/controls/DataGrid/Izenda.Table';
import Paginator from 'share/components/controls/DataGrid/helpers/Izenda.Paginator';
import CategoryColumn from './Izenda.CategoryColumn';
import FieldLevelColumn from './Izenda.FieldLevelColumn';
import ExpressionLevelColumn from './Izenda.ExpressionLevelColumn';
import DynamicColumn from './Izenda.DynamicColumn';
import RelationshipStore from 'logic/Izenda.RelationshipStore';
import OverflowText from 'share/components/controls/Izenda.OverflowText';
import Validator from 'share/components/hoc/Izenda.Validator';
import connect from 'share/enhancers/Izenda.Connect';
import CustomViewPopup from './Izenda.CustomViewPopup';
import RelationshipPopup from './Izenda.RelationshipPopup';
import { constant, matches, cond, isEmpty } from 'lodash/fp';
const Input = Validator()(require('share/components/controls/Izenda.Input'));
const QuerySourceType = QuerySourceTypeConfig.QUERY_SOURCE_TYPE;

@connect()
class QuerySourceTypeList extends Component {
  static propTypes = {
    store: React.PropTypes.object.isRequired,
    searchProperty: React.PropTypes.string,
    searchValue: React.PropTypes.string
  };

  render() {
    const props = this.props;
    const store = this.props.store;
    const newHeight =
      props.height > 0 && QuerySourceType.FUNCTION === store.querySourceType
        ? props.height - constants.DEFAULT_REST_HEIGHT
        : constants.DEFAULT_TABLE_HEIGHT;
    const TABLE_DEF = TableDefinition.QuerySourceType;

    return (
      <section>
        <Table
          dataSource={store.querySourceList}
          rowProps={this.getRowProps}
          onSortChange={this.onSortChange}
          sortInfo={store.pageInfo.colSortDirs}
          minHeight={newHeight}
          maxHeight={newHeight}
          isLoading={props.isLoading}
        >
          {/* Category */}
          {QuerySourceType.FUNCTION !== store.querySourceType && (
            <Column {...TABLE_DEF['category']} cell={CategoryColumn.render.bind(this)} />
          )}

          {/* Database name */}
          <Column {...TABLE_DEF['dbName']} cell={this.renderDbName} />

          {/* Database source name */}
          {QuerySourceType.FUNCTION !== store.querySourceType && (
            <Column {...TABLE_DEF['dbSourceName']} cell={this.renderDbSourceName} />
          )}

          {/* Function name */}
          {QuerySourceType.FUNCTION === store.querySourceType && (
            <Column {...TABLE_DEF['functionName']} cell={this.renderFunctionName} />
          )}

          {/* Database source alias */}
          {QuerySourceType.FUNCTION !== store.querySourceType && (
            <Column {...TABLE_DEF['dbSourceAlias']} cell={this.renderDbSourceAlias} />
          )}

          {/* Dynamic */}
          {QuerySourceType.STORED_PROC === store.querySourceType && (
            <Column {...TABLE_DEF['dynamic']} align={'center'} cell={DynamicColumn.render.bind(this)} />
          )}

          {/* Returned Value */}
          {QuerySourceType.FUNCTION === store.querySourceType && (
            <Column {...TABLE_DEF['returnedValue']} cell={this.renderReturnedValue} />
          )}

          {/* Input Params */}
          {QuerySourceType.FUNCTION === store.querySourceType && (
            <Column {...TABLE_DEF['inputParams']} cell={this.renderInputParams} />
          )}

          {/* Field Level */}
          {QuerySourceType.FUNCTION === store.querySourceType && (
            <Column {...TABLE_DEF['fieldLevel']} align={'center'} cell={FieldLevelColumn.render.bind(this)} />
          )}

          {/* Expression Level */}
          {QuerySourceType.FUNCTION === store.querySourceType && (
            <Column {...TABLE_DEF['expressionLevel']} align={'center'} cell={ExpressionLevelColumn.render} />
          )}

          {/* buttons: relations */}
          {QuerySourceType.FUNCTION !== store.querySourceType && (
            <Column {...TABLE_DEF['controlBtn']} align={'center'} cell={this.renderButtonCtrls} />
          )}
        </Table>

        <Paginator
          onChange={this.goToPage}
          onDropdownChange={this.onPageSizeChange}
          totalRecords={store.totalRecords}
          pageSize={store.pageInfo.pageSize}
          pageIndex={store.pageInfo.pageIndex}
        />
      </section>
    );
  }

  getRowProps = spDetails => {
    const store = this.props.store;

    if (!store.selectedQuerySource) {
      return null;
    }

    return {
      className: spDetails.isSelected ? 'selected-row' : ''
    };
  };

  /**
   * This function is to render column Database Name
   * {params} querySourceDetailsVM
   */
  renderDbName = querySourceDetailsVM => {
    return (
      <span
        className="truncate overflow-text"
        title={querySourceDetailsVM.getDbName()}
        onClick={this.showQuerySourceDetail.bind(this, querySourceDetailsVM)}
      >
        {querySourceDetailsVM.getDbName()}
      </span>
    );
  };

  renderReturnedValue = querySourceDetailsVM => {
    const returnValue = querySourceDetailsVM.getReturnedValue();
    return <OverflowText text={returnValue} tooltip={returnValue} />;
  };

  renderInputParams = querySourceDetailsVM => {
    const inputParams = querySourceDetailsVM.getInputParams();
    return <OverflowText text={inputParams} tooltip={inputParams} />;
  };

  renderFunctionName = querySourceDetailsVM => {
    const functionName = querySourceDetailsVM.getDbSourceName();
    let className, title;
    switch (querySourceDetailsVM.details.physicalChange) {
      case DETECTED_CHANGE_LEVEL.MODIFIED:
        className = 'icon-attention warning-color';
        title = constants.FUNCTION_CHANGE;
        break;
      case DETECTED_CHANGE_LEVEL.DELETED:
        className = 'icon-fields fail-color';
        title = constants.DELETED_FUNCTION;
        break;
      case DETECTED_CHANGE_LEVEL.ADDED:
        className = 'icon-fields success-color';
        title = constants.FUNCTION_ADD;
        break;
    }

    return (
      <div>
        <span title={title}>{functionName}</span>
        {className && <span className={className} title={title} />}
      </div>
    );
  };

  renderDbSourceName = querySourceDetailsVM => {
    const props = this.props;
    const store = props.store;
    const renderChangedIcon = () => {
      const change = querySourceDetailsVM.details.physicalChange;
      const approval = querySourceDetailsVM.details.approval;
      const querySourceFieldApprovalLevel = constants.APPROVAL_LEVEL.QUERY_SOURCE_FIELD;
      const querySourceApprovalLevel = constants.APPROVAL_LEVEL.QUERY_SOURCE;
      const noneChangeLevel = constants.DETECTED_CHANGE_LEVEL.NONE;
      const deletedChangeLevel = constants.DETECTED_CHANGE_LEVEL.DELETED;
      const addedChangeLevel = constants.DETECTED_CHANGE_LEVEL.ADDED;

      const isQuerySourceAdd = approval === querySourceApprovalLevel && change === addedChangeLevel;

      const isEdit =
        !isQuerySourceAdd &&
        (approval < querySourceFieldApprovalLevel && change !== noneChangeLevel && change !== deletedChangeLevel);

      const isDelete = approval < querySourceFieldApprovalLevel && change === deletedChangeLevel;

      const className = classnames({
        'icon-attention warning-color': isEdit,
        'icon-fields fail-color': isDelete,
        'icon-fields success-color': isQuerySourceAdd
      });

      const title = cond([
        [matches({ isEdit: true }), constant(constants.DATA_SOURCE_CHANGES)],
        [matches({ isDelete: true }), constant(constants.DELETED_DATA_SOURCE)],
        [matches({ isQuerySourceAdd: true }), constant(constants.NEW_DATA_SOURCE)]
      ])({ isEdit, isDelete, isQuerySourceAdd });

      return <span className={className} title={title} />;
    };

    const renderExecError = () => {
      const isError = !isEmpty(querySourceDetailsVM.executeErrorMessage);
      const classes = classnames({ 'icon-attention': isError }, 'fail-color');

      return <span className={classes} title={querySourceDetailsVM.executeErrorMessage} />;
    };

    const permissionKeys = props.authorizationConstants.PERMISSION_KEY;
    const isCustomQuerySource = querySourceDetailsVM.details.isCustomQuerySource;
    const canEditCustomView = props.authorization.checkPrivileges([permissionKeys.DataSetupDataModelCustomViewEdit]);

    const classes = classnames({
      'source-name': isCustomQuerySource && canEditCustomView,
      u_curPointer: isCustomQuerySource && canEditCustomView
    });

    return (
      <span
        className="truncate overflow-text"
        onClick={this.showQuerySourceDetail.bind(this, querySourceDetailsVM)}
        title={querySourceDetailsVM.getDbSourceName()}
      >
        <span className={classes} onClick={onEditCustomView.bind(this)}>
          {querySourceDetailsVM.getDbSourceName()}
        </span>
        {renderChangedIcon()}
        {renderExecError()}
      </span>
    );

    function onEditCustomView() {
      if (isCustomQuerySource && canEditCustomView) {
        props.openPopup(CustomViewPopup, {
          store,
          detail: querySourceDetailsVM.details
        });
      }
    }
  };

  showQuerySourceDetail = (querySourceDetailsVM, event) => {
    event.preventDefault();
    const props = this.props;
    const { loader, store } = props;

    store.alertMessage = CommonMessages.EMPTY_MESSAGE;
    if (store.selectedQuerySource.getId() === querySourceDetailsVM.getId()) {
      return;
    }

    props.confirmDirtyForm(store.selectedQuerySource.hasDetailChange).then(() => {
      loader.startLoading();
      store.setShowingQuerySource(querySourceDetailsVM).finally(loader.endLoading.bind(loader));
    });
  };

  /**
   * This function is to render column Database Source Alias
   * {params} querySourceDetailsVM
   */
  renderDbSourceAlias = (querySourceDetailsVM, key, index) => {
    const classes = classnames('form-group', { 'has-error': querySourceDetailsVM.aliasError });
    const alias = querySourceDetailsVM.getDbSourceAlias() || '';
    return (
      <div className={classes}>
        <Input
          type="text"
          className="form-control"
          value={alias}
          onChange={updateValue}
          title={alias}
          field={'QuerySources[' + index + '].Alias'}
        />
      </div>
    );

    function updateValue(e) {
      return querySourceDetailsVM.setDbSourceAlias(e.target.value);
    }
  };

  renderButtonCtrls = item => {
    const props = this.props;
    const { store, localization, authorization } = props;
    const permissionKeys = props.authorizationConstants.PERMISSION_KEY;
    const isCustomQuerySource = item.details.isCustomQuerySource;
    const canDeleteCustomView = authorization.checkPrivileges([permissionKeys.DataSetupDataModelCustomViewDelete]);

    const onRelationshipClick = () => {
      this.handleActionShowRelationshipDataSource(item);
    };
    const onDeleteCustomView = () => {
      props
        .confirmFn('Confirmation', {
          description: localization.getText('QUERY_SOURCE_TYPE_CONFIG_MESSAGE_DELETE_CUSTOM_VIEW')
        })
        .then(() => store.deleteCustomQuerySource(item))
        .then(({ success }) => success && store.reload());
    };

    return (
      <div>
        <a onClick={onRelationshipClick}>
          <span className="icon-resize-small" />
        </a>
        {isCustomQuerySource &&
          canDeleteCustomView && (
            <a onClick={onDeleteCustomView}>
              <span className="icon-cancel" />
            </a>
          )}
      </div>
    );
  };

  handleActionShowRelationshipDataSource = item => {
    const props = this.props;
    const store = props.store;

    const datasource = !!item.details ? item.details : null;
    const relationship = new RelationshipStore(datasource);
    relationship.initData(true);
    store.relationship = relationship;
    props.openPopup(RelationshipPopup, { store });
  };

  onSortChange = (columnKey, sortDir) => {
    const props = this.props;
    const { store, loader } = props;

    return props.confirmDirtyForm(store.hasChange).then(() => {
      loader.startLoading();
      store.pageInfo.setColSortDirs(columnKey, sortDir);
      return store.loadQuerySources().finally(loader.endLoading.bind(loader));
    });
  };

  onPageSizeChange = pageSize => {
    const props = this.props;
    const { store, loader } = props;

    return props.confirmDirtyForm(store.hasChange).then(() => {
      loader.startLoading();
      store.pageInfo.setPageSize(pageSize);
      return store.goToPage(1).finally(loader.endLoading.bind(loader));
    });
  };

  goToPage = pageIndex => {
    const props = this.props;
    const { store, loader } = props;

    return props.confirmDirtyForm(store.hasChange).then(() => {
      loader.startLoading();
      return store.goToPage(pageIndex).finally(loader.endLoading.bind(loader));
    });
  };
}

export default QuerySourceTypeList;



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/DataModel/QuerySourceType/Izenda.QuerySourceTypeList.js